/*
 * Ryan O'Dowd
 * 2024-03-03
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useState,
} from 'react';
import {
  TouchableOpacity,
  View,
} from 'react-native';
import Accordion from '@components/common/Accordion';
import AssignmentProgressDialog from './AssignmentProgressDialog';
import Assignments from './Assignments';
import CPassage from '@classes/Passage';
import EditAssignmentDialog from '../../EditAssignmentDialog'; // @TODO: should probably move this location
import Icon from '@react-native-vector-icons/material-design-icons';
import IconButton from '@components/common/IconButton';
import LinearProgress from '@components/common/LinearProgress';
// @TODO: import PublishedStatus from '../../PublishedStatus';
import Text from '@components/common/Text';
import Tooltip from '@components/common/Tooltip';
import {
  duplicateAssignment,
} from '@utils/store/actions';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const Assignment = (props: AssignmentProps) => {
  const styles = hooks.useStyles(getStyles);
  const dispatch = hooks.useTypedDispatch();

  const goalProgress = hooks.useTypedSelector((state) => state.goalProgress[props.goal.id]) || {};
  const numLessonsProgressCompletedByAllStudents = Object.values(goalProgress).flatMap((student) => Object.entries(student)).filter(([assignmentId, __gp]) => +assignmentId === props.assignment.id).map(([__assignmentId, gp]) => gp.nlc).reduce((a, b) => a + b, 0);
  const numLessonsRequiredByAllStudents = Object.values(goalProgress).flatMap((student) => Object.entries(student)).filter(([assignmentId, __gp]) => +assignmentId === props.assignment.id).map(([__assignmentId, gp]) => gp.nlr).reduce((a, b) => a + b, 0);

  let unit = props.assignment.assignment_object.must_memorize ? 'levels' : 'lessons';
  if (numLessonsRequiredByAllStudents === 1) {
    unit = props.assignment.assignment_object.must_memorize ? 'level' : 'lesson';
  }

  const [_isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [_expanded, setExpanded] = useState(false);
  const [_isProgressDialogOpen, setIsProgressDialogOpen] = useState(false);

  let iconName: any = 'school'; // assume assignment.assignment_type === 'verse_memory' and not must_memorize
  if (props.assignment.assignment_type === 'verse_memory' && props.assignment.assignment_object.must_memorize) {
    iconName = 'test-tube';
  } else if (props.assignment.assignment_type === 'reading') {
    iconName = 'book-open-page-variant';
  } else if (props.assignment.assignment_type === 'lecture') {
    iconName = 'bullhorn';
  } else if (props.assignment.assignment_type === 'video') {
    iconName = 'video';
  }

  const passage = props.assignment.assignment_object.passage ? CPassage.createFromObject(props.assignment.assignment_object.passage) : null;

  return (
    <React.Fragment>
      <Accordion
        containerStyles={styles.accordionWrapper}
        theme='white'
        onChange={(expanded) => setExpanded(expanded)}
        leftItem={
          <TouchableOpacity
            style={styles.dragIconWrapper}
            onPressIn={props.drag}
          >
            <Icon
              name='drag'
              size={24}
              style={styles.dragIcon}
            />
          </TouchableOpacity>
        }
        title={
          <View style={styles.editRow}>
            <View style={styles.topRow}>
              <Icon name={iconName} style={styles.typeIcon} />
              <View style={styles.fullTitleWrapper}>
                {['verse_memory', 'reading'].includes(props.assignment?.assignment_type)
                  ? (
                    <React.Fragment>
                      <h3 style={styles.assignmentName}>{passage.prettyPassageName()}</h3>{/* @TODO: change tag type? and styles */}
                      <h5 style={styles.assignmentName}>{props.assignment.name}</h5>{/* @TODO: change tag type? and styles */}
                    </React.Fragment>
                  )
                  : (
                    <React.Fragment>
                      <h3 style={styles.assignmentName}>{props.assignment.name}</h3>{/* @TODO: change tag type? and styles */}
                    </React.Fragment>
                  )
                }
                <Text style={styles.subTitle}>{utilities.prettyAvailablityDateRange(props.assignment.date_available, props.assignment.date_deadline)}</Text>
              </View>
            </View>
            <View style={styles.actionsWrapper}>
              {/* @TODO: tooltip on hover to show the number of students out of the number of students (non-simplified fraction is better than percentage) */}
              {/* @TODO: on click, open dialog with detailed breakdown of class */}
              {props.goal.classroom_goal_metadata.status === 'published' &&
                <Tooltip title={`${numLessonsProgressCompletedByAllStudents} of ${numLessonsRequiredByAllStudents} ${unit} completed`}>
                  <TouchableOpacity
                    style={styles.progressWrapper}
                    onPress={(event) => {
                      event.stopPropagation();
                      setIsProgressDialogOpen(true);
                    }}
                  >{/* @TODO: don't love the jump when the edit icon is visible */}
                    <LinearProgress progress={numLessonsProgressCompletedByAllStudents / numLessonsRequiredByAllStudents} />
                  </TouchableOpacity>
                </Tooltip>
              }
              {_expanded &&
                <React.Fragment>
                  {props.goal.classroom_goal_metadata.status === 'draft' && props.assignment.assignment_type === 'verse_memory' &&
                    <Tooltip title='Duplicate assignment'>
                      <IconButton
                        icon='content-copy'
                        iconColor={styles.colors.primary}
                        onPress={(event) => {
                          event.stopPropagation();
                          dispatch(duplicateAssignment(props.goal.classroom_goal_metadata.classroom_id, props.goal.id, props.module.id, props.assignment.id));
                        }}
                      />
                    </Tooltip>
                  }
                  <Tooltip title='Edit assignment'>
                    <IconButton
                      icon='pencil'
                      iconColor={styles.colors.primary}
                      onPress={() => setIsEditDialogOpen(true)}
                    />
                  </Tooltip>
                </React.Fragment>
              }
              {/* @TODO: only goals have draft/published status right now
              <PublishedStatus status={props.assignment.status} assignment={props.assignment} />
              */}
            </View>
          </View>
        }
      >
        {props.assignment.assignment_type === 'lecture' && <Assignments.Lecture {...props.assignment.assignment_object} goalId={props.goal.id} />}
        {props.assignment.assignment_type === 'verse_memory' && <Assignments.VerseMemory {...props.assignment.assignment_object} passage={passage} goalId={props.goal.id} />}
        {props.assignment.assignment_type === 'reading' && <Assignments.Reading {...props.assignment.assignment_object} passage={passage} goalId={props.goal.id} />}
      </Accordion>
      {_isEditDialogOpen && <EditAssignmentDialog goal={props.goal} module={props.module} assignment={props.assignment} onClose={() => setIsEditDialogOpen(false)} />}
      {_isProgressDialogOpen && <AssignmentProgressDialog goal={props.goal} assignment={props.assignment} onClose={() => setIsProgressDialogOpen(false)} />}
    </React.Fragment>
  );
};

class AssignmentProps {
  goal: any;
  module: any;
  assignment: any;
  drag: any;
};

export default Assignment;
